import { ReactNode, createContext, useCallback, useMemo } from "react";
import useLocalStorage from "hooks/useLocalStorage";
import { APP_CONFIG } from "configs/app.config";
import { ApiTheme, ApiUserThemeOverrides } from "types/api-themes";

const initialState = {
  ...APP_CONFIG.THEME_SETTINGS,
  onSetAppTheme: (theme: ApiTheme) => {},
  onResetAppTheme: () => {},
  onToggleLayout: () => {},
  onUpdateThemeWithOverrides: (themeOverrides: ApiUserThemeOverrides) => {},
};

const AppThemeContext = createContext(initialState);

type AppThemeProviderProps = {
  children: ReactNode;
};

function AppThemeProvider({ children }: AppThemeProviderProps) {
  const [themeSettings, setThemeSettings] = useLocalStorage("site-theme-settings", {
    themeMode: initialState.themeMode,
    themeLayout: initialState.themeLayout,
    themeStretch: initialState.themeStretch,
    themeContrast: initialState.themeContrast,
    themeDirection: initialState.themeDirection,
    primaryColourSet: initialState.primaryColourSet,
    primaryFont: initialState.primaryFont,
    highlightColor: initialState.highlightColor
  });


  const onSetAppTheme = useCallback(
    (theme: ApiTheme) => {
      setThemeSettings({
        ...themeSettings,
        themeMode: theme.mode,
        themeLayout: theme.layout,
        themeStretch: theme.stretchMode,
        themeContrast: theme.contrast,
        themeDirection: theme.direction,
        primaryColorSet: {
          contrastText: theme.primaryColourSet.contrastText,
          dark: theme.primaryColourSet.dark,
          darker: theme.primaryColourSet.darker,
          light: theme.primaryColourSet.light,
          lighter: theme.primaryColourSet.lighter,
          main: theme.primaryColourSet.main,
          name: theme.primaryColourSet.name,
        },
      });
    },
    [setThemeSettings, themeSettings]
  );

  const onResetAppTheme = useCallback(() => {
    setThemeSettings({
      ...themeSettings,
      themeMode: initialState.themeMode,
      themeLayout: initialState.themeLayout,
      themeStretch: initialState.themeStretch,
      themeContrast: initialState.themeContrast,
      themeDirection: initialState.themeDirection,
      primaryColorSet: initialState.primaryColourSet,
    });
  }, [setThemeSettings, themeSettings]);

  const onUpdateThemeWithOverrides = useCallback(
    (themeOverrides: ApiUserThemeOverrides) => {
      setThemeSettings({
        ...themeSettings,
        isUserThemeOverridesApplied: true,
        themeMode: themeOverrides.mode,
        themeLayout: themeOverrides.layout,
        themeStretch: themeOverrides.stretchMode,
        themeContrast: themeOverrides.contrast,
        themeDirection: themeOverrides.direction,
      });
    },
    [setThemeSettings, themeSettings]
  );

  
  const onToggleLayout = useCallback(() => {
    const themeLayout = themeSettings.themeLayout === 'vertical' ? 'mini' : 'vertical';
    setThemeSettings({ ...themeSettings, themeLayout });
  }, [setThemeSettings, themeSettings]);


  const memoizedValue = useMemo(
    () => ({
      ...themeSettings,
      onSetAppTheme,
      onResetAppTheme,
      onUpdateThemeWithOverrides,
      onToggleLayout
    }),
    [themeSettings, onSetAppTheme, onResetAppTheme, onUpdateThemeWithOverrides, onToggleLayout]
  );
  return <AppThemeContext.Provider value={memoizedValue}>{children}</AppThemeContext.Provider>;
}

export { AppThemeProvider, AppThemeContext };
